import {
    updateBrandLetterhead,
    type DocumentNumberingPrefix,
    type NumberedDocumentType,
} from '@api/organization';
import useToaster from '@hooks/toaster/useToaster';
import { Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { useState, type FC } from 'react';

interface AddPrefixModalProps {
    opened: boolean;
    onClose: () => void;
    documentType: NumberedDocumentType;
    /**
     * The prefixes already defined, passed in because the SAVE REPLACES the list for this
     * document type - the server's contract is "the prefixes are now these", so a modal that
     * sent only the new one would silently delete the rest.
     */
    existing: DocumentNumberingPrefix[];
    /** Fired with the saved value so the caller can select it and fill the number in. */
    onSaved: (prefix: string) => void;
}

/**
 * Mint a prefix without leaving the order.
 *
 * The Settings screen is where prefixes are managed; this exists because the moment somebody
 * discovers they need one is halfway through raising an order, and sending them to Settings
 * loses the order they were writing. Same reasoning as "New manufacturer" beside the picker.
 *
 * A first prefix becomes the default (a brand defining exactly one plainly means to use it);
 * later ones do not steal that - changing the default is a deliberate act, done in Settings.
 */
const AddPrefixModal: FC<AddPrefixModalProps> = ({
    opened,
    onClose,
    documentType,
    existing,
    onSaved,
}) => {
    const { showToastError } = useToaster();
    const [value, setValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const trimmed = value.trim();
    const duplicate = existing.some((entry) => entry.value.trim() === trimmed);

    const handleClose = () => {
        setValue('');
        setIsSaving(false);
        onClose();
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await updateBrandLetterhead({
                documentNumbering: {
                    [documentType]: {
                        prefixes: [
                            ...existing,
                            { value: trimmed, isDefault: existing.length === 0 },
                        ],
                    },
                },
            });
            onSaved(trimmed);
            handleClose();
        } catch (err: any) {
            showToastError({
                title: 'Could not save the prefix',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
            setIsSaving(false);
        }
    };

    return (
        <Modal opened={opened} onClose={handleClose} title="Add a prefix" size="sm" centered>
            <Stack gap="md">
                <TextInput
                    label="Prefix"
                    description="Used exactly as typed - include the separator if you want one"
                    placeholder="BANGE-"
                    value={value}
                    onChange={(e) => setValue(e.currentTarget.value)}
                    error={duplicate ? 'That prefix already exists' : undefined}
                    data-autofocus
                />

                {/*
                  * The number it will produce, because the separator is the part people get
                  * wrong: 'BANGE' and 'BANGE-' read identically in a text box until you see
                  * BANGE0001. Same preview the Settings screen shows.
                  */}
                {trimmed && !duplicate && (
                    <Text fz={12} c="dimmed">
                        First order: <strong>{trimmed}0001</strong>
                    </Text>
                )}

                <Group justify="flex-end" gap={8}>
                    <Button variant={ButtonVariant.DEFAULT} onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        loading={isSaving}
                        disabled={!trimmed || duplicate}
                    >
                        Save prefix
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default AddPrefixModal;
